import { STATE_CODES } from '@aspendental/shared-components-web';
import { SelectInput, TextInput } from '@aspendental/shared-forms-web';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { addressForm } from './Address.helpers';
import AddressAutoComplete from './AddressAutoComplete';

import { usePatientInformationStore } from '@src/store';

export default function Address({ form, smartyKey }: { form: UseFormReturn; smartyKey: string }) {
	const { t } = useTranslation('index');
	const { setCity, setState, setZipCode } = usePatientInformationStore((store) => store);

	const zipCode = form.watch('zipCode');

	useEffect(() => {
		const fetchZipData = async () => {
			try {
				const response = await fetch(process.env.APP_PATH + `/api/clear-choice/ziptastic/?zip=${zipCode}`, {
					method: 'GET',
				});
				const data = await response.json();

				if (!response.ok) {
					throw new Error('Network response was not ok');
				} else if (response.ok && data) {
					setState(data[0].state_short);
					setCity(data[0].city);
					setZipCode(zipCode);
					form.setValue(addressForm.city.name, data[0].city);
					form.setValue(addressForm.state.name, data[0].state_short);
				}
			} catch (error) {
				console.error('Error fetching zip data:', error);
			}
		};

		if (zipCode && zipCode.length === 5) {
			fetchZipData();
		}
	}, [zipCode, setCity, setState, setZipCode, form]);

	return (
		<Grid mt={4}>
			<Typography variant="header4" color="text.primary">
				{t('address')}
			</Typography>
			<Grid container spacing={{ md: 2.5, xs: 1.7 }} pt={{ md: 2.5, xs: 1.7 }} alignItems="stretch">
				<Grid
					item
					xs={6}
					md={12}
					data-private
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
					}}
				>
					<AddressAutoComplete
						name={addressForm.address.name}
						form={form}
						smartyKey={smartyKey}
						setAddress={(address: string) => form.setValue(addressForm.address.name, address)}
					/>
				</Grid>
				<Grid
					item
					xs={6}
					md={5}
					data-private
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
					}}
				>
					<TextInput name={addressForm.city.name} label={t('city')} />
				</Grid>
				<Grid
					item
					xs={6}
					md={3}
					data-private
					sx={{
						display: 'flex',
						flexDirection: 'column',
						'& .MuiInputBase-root': {
							height: '56px',
						},
					}}
				>
					<SelectInput
						name={addressForm.state.name}
						label={t('state')}
						options={STATE_CODES.map((option) => ({ label: option.state, value: option.code }))}
					/>
				</Grid>
				<Grid
					item
					xs={6}
					md={4}
					data-private
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
					}}
				>
					<TextInput
						name={addressForm.zipCode.name}
						label={t('zipCode')}
						maxLength={5}
						sx={{ minHeight: '56px' }}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
